// Оформление урока: ЧТО показать в пикере и ЧТО разослать по блокам.
// Тема лежит на уроке (`lesson.theme`), паттерн — на каждом блоке
// (`patternType`, поля-близнеца у урока нет), поэтому пикер собирает своё
// значение из двух источников, а рассылка пишет в оба.
import {
  DEFAULT_LESSON_THEME,
  LESSON_THEMES,
  resolveLessonThemeId,
  resolvePatternId,
  type BlockPatternId,
  type LessonThemeId,
} from '@teach/ui'
import type { Block, BlockCreate } from '@/types/blocks'
import { toBlockCreate } from './duplicateBlock'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

/** Типы, которые вообще носят фон. У quiz/match/video `bgColor` осознанно
 *  `null` (см. blockDefaults) — им не рассылается ни тема, ни паттерн. */
const LOOK_TYPES: ReadonlySet<Block['type']> = new Set(['info', 'design', 'article'])

export const lookBlocks = (blocks: Block[]): Block[] => blocks.filter((b) => LOOK_TYPES.has(b.type))

export interface LessonLook {
  theme: LessonThemeId | null
  pattern: BlockPatternId | null
  /** Блоки разошлись по паттерну (так умеет MCP-агент): подсвечивать нечего. */
  isMixedPattern: boolean
  /** Якорь хотя бы одного экрана разошёлся с темой урока — так выглядит
   *  оборванная на середине рассылка: `lesson.theme` уже новая, часть блоков
   *  осталась старой. Тост об этом живёт секунды и после перезагрузки не
   *  возвращается, поэтому расхождение объявляет сама секция. */
  isMixedTheme: boolean
  /** Есть ли в уроке хоть один блок, носящий оформление. Пустой урок и урок
   *  из одних квизов — не ошибка, но паттерн там применять не к чему. */
  hasLookBlocks: boolean
}

const common = <T>(values: T[]): T | null =>
  values.length > 0 && values.every((v) => v === values[0]) ? values[0] : null

const knownTheme = (value: string | null | undefined): LessonThemeId | null =>
  LESSON_THEMES.find((t) => t.id === value)?.id ?? null

// Тот же путь чтения, что у превью (`previews/blockBackground.ts`): у легаси-info
// тема лежит во ВЛОЖЕННОМ `body.bgColor` — так писал старый InfoBlockEditor.
// Без фолбэка превью показывало цвет, а пикер не подсвечивал ничего.
const blockThemeId = (b: Block): LessonThemeId | null =>
  resolveLessonThemeId(b.bgColor ?? (b.type === 'info' ? b.body?.bgColor : null))

// Тема, в которой экран ВЫГЛЯДИТ. Блока без фона не бывает: пустой цвет — базовое состояние,
// кремовая тема, поэтому такой урок показывает её выбранной, а не просит выбрать.
// Незнакомый цвет остаётся `null`: он рисуется плоско, мимо темы.
const shownThemeId = (b: Block): LessonThemeId | null =>
  (b.bgColor ?? (b.type === 'info' ? b.body?.bgColor : null))
    ? blockThemeId(b)
    : DEFAULT_LESSON_THEME.id

export const readLessonLook = (
  lessonTheme: string | null | undefined,
  blocks: Block[],
): LessonLook => {
  const own = lookBlocks(blocks)
  const patterns = own.map((b) => resolvePatternId(b.patternType))
  const pattern = common(patterns)
  const themes = own.map(shownThemeId)
  // Фолбэк на якорь блоков нужен легаси-урокам: поле `theme` у них ещё
  // `null`, а тема в данных явно есть.
  const theme = knownTheme(lessonTheme) ?? common(themes)
  return {
    theme,
    pattern,
    isMixedPattern: pattern === null && patterns.length > 1 && new Set(patterns).size > 1,
    isMixedTheme: theme !== null && themes.some((t) => t !== theme),
    hasLookBlocks: own.length > 0,
  }
}

export interface LookNotesInput {
  look: LessonLook
  /** Блоки урока ПРИЕХАЛИ. Пока нет — не утверждаем о них ничего. */
  isLoaded: boolean
  /** Контролы погашены по внешней причине (не автор / идёт применение). */
  locked: boolean
}

/** Поясняющие строки под пикерами — то, что автор иначе видит как молчаливо
 *  серые карточки или как «выбор сделан», которого в экранах нет. */
export const lookNotes = ({ look, isLoaded, locked }: LookNotesInput): string[] => {
  if (!isLoaded) return []
  // Урок без экранов с фоном (пустой или одни квизы) — молчим: подпись про это
  // заказчик снял как лишнюю, выбор темы при этом всё равно записывается в урок.
  if (!look.hasLookBlocks) return []
  const notes: string[] = []
  if (look.isMixedPattern) notes.push(messagesNow(messages).lookMixedPattern)
  // Совет «выберите ещё раз» выполним, только пока пикер живой: на проверке курса он погашен.
  if (look.isMixedTheme && !locked) notes.push(messagesNow(messages).lookMixedTheme)
  // Темы нет только у блоков с незнакомыми цветами: без подписи автор видит серые
  // карточки и ни слова почему. Пустой цвет сюда не попадает, это кремовая тема.
  if (look.theme === null && !locked) notes.push(messagesNow(messages).lookPickThemeFirst)
  return notes
}

export const lookUpdates = (
  blocks: Block[],
  look: { theme: LessonThemeId; pattern: BlockPatternId | null },
): Array<{ id: number; body: BlockCreate }> => {
  const anchor = LESSON_THEMES.find((t) => t.id === look.theme)?.anchor ?? LESSON_THEMES[0].anchor
  return lookBlocks(blocks).map((b) => ({
    id: b.id,
    // Тело блочного PUT — ЦЕЛИКОМ (§3.2): собираем из блока, а не из полей.
    body: { ...toBlockCreate(b, b.order), bgColor: anchor, patternType: look.pattern },
  }))
}
